import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Calculator, LogOut, User as UserIcon, Menu } from 'lucide-react';
import { authApi, type User } from '@/lib/api/auth';
import { performLogoutAndRedirect } from '@/lib/logout';
import LogoutConfirmModal from '@/components/auth/LogoutConfirmModal';
import QuickEmployeeSearch from '@/components/QuickEmployeeSearch';

const TITLE_MAP: { match: string; title: string }[] = [
  { match: '/banks/bsi', title: 'بانک صادرات' },
  { match: '/banks', title: 'بانک‌ها' },
  { match: '/bsi-internet-bank', title: 'بانک صادرات' },
  { match: '/accounting-bank-movements', title: 'ثبت پرداخت/دریافت بانکی' },
  { match: '/accounting-reports/account-turnover', title: 'گزارش گردش حساب' },
  { match: '/accounting-settings/print', title: 'تنظیمات چاپ حسابداری' },
  { match: '/accounting-settings/cheque', title: 'مهر و امضای چک' },
  { match: '/accounting-settings', title: 'تنظیمات' },
  { match: '/cheque-books/register', title: 'ثبت برگه چک' },
  { match: '/cheque-books', title: 'دسته‌چک' },
  { match: '/accounting-documents/', title: 'جزئیات سند حسابداری' },
  { match: '/accounting-documents', title: 'ثبت سند حسابداری' },
  { match: '/journal-entries', title: 'ثبت سند روزنامه' },
  { match: '/general-ledger', title: 'دفتر کل' },
  { match: '/subsidiary-ledger', title: 'دفتر معین' },
  { match: '/trial-balance', title: 'تراز آزمایشی' },
  { match: '/balance-sheet', title: 'ترازنامه' },
  { match: '/profit-loss', title: 'سود و زیان' },
  { match: '/payroll-calculator', title: 'محاسبه حقوق و دستمزد' },
  { match: '/payroll-batch', title: 'پرداخت حقوق دسته‌ای' },
  { match: '/invoice-maker', title: 'فاکتورساز' },
  { match: '/employees/', title: 'پروفایل کارمند' },
  { match: '/employees', title: 'مدیریت کارمندان' },
];

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const pageTitle = useMemo(() => {
    if (location.pathname === '/') return 'داشبورد مالی';
    const bsiMatch = location.pathname.match(/^\/banks\/bsi(?:\/([^/]+))?$/);
    if (bsiMatch) {
      const section = bsiMatch[1];
      if (!section) return 'بانک صادرات';
      const titles: Record<string, string> = {
        login: 'بانک صادرات · ورود',
        accounts: 'بانک صادرات · حساب‌ها',
        last30: 'بانک صادرات · سی گردش آخر',
        period: 'بانک صادرات · گردش دوره‌ای',
        destinations: 'بانک صادرات · حساب‌های مقصد',
        transfer: 'بانک صادرات · انتقال صادراتی',
        interbank: 'بانک صادرات · ساتنا/پایا/پل',
        'batch-local': 'بانک صادرات · صادرات دسته‌ای',
        batch_local: 'بانک صادرات · صادرات دسته‌ای',
        'batch-paya': 'بانک صادرات · پایا دسته‌ای',
        batch_paya: 'بانک صادرات · پایا دسته‌ای',
        pending: 'بانک صادرات · قابل امضا',
        iban: 'بانک صادرات · استعلام شبا',
        receipt: 'بانک صادرات · رسید',
        cheque: 'بانک صادرات · چک',
      };
      return titles[section] ?? 'بانک صادرات';
    }
    const hit = TITLE_MAP.find((t) => location.pathname.startsWith(t.match));
    return hit?.title ?? 'حسابداری';
  }, [location.pathname]);

  useEffect(() => {
    authApi
      .fetchUser()
      .then(setUser)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <header className="z-10 flex h-12 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 shadow-sm">
        <div className="flex min-w-0 flex-1 items-center gap-2 text-gray-800">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              className="-m-2 rounded-lg p-2 hover:bg-gray-100 md:hidden"
              aria-label="باز کردن منو"
            >
              <Menu size={22} />
            </button>
          )}
          <Calculator size={20} className="shrink-0 text-amber-600" />
          <span className="min-w-0 truncate text-sm font-semibold md:text-base">{pageTitle}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <QuickEmployeeSearch />
        {!loading && user && (
          <>
            <div className="hidden max-w-[10rem] items-center gap-2 text-sm text-gray-600 sm:flex md:max-w-xs">
              <UserIcon size={16} className="shrink-0" />
              <span className="truncate">{user.name || user.username}</span>
            </div>
            <button
              type="button"
              onClick={() => setLogoutOpen(true)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-red-600 transition-colors hover:bg-red-50"
              title="خروج"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">خروج</span>
            </button>
          </>
        )}
        </div>
      </header>
      <LogoutConfirmModal
        open={logoutOpen}
        isLoading={loggingOut}
        onCancel={() => {
          if (!loggingOut) setLogoutOpen(false);
        }}
        onConfirm={async () => {
          setLoggingOut(true);
          try {
            await performLogoutAndRedirect();
          } finally {
            setLoggingOut(false);
          }
        }}
      />
    </>
  );
};

export default Header;
